
import React from 'react';
import { MessageSquarePlus, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface CreateThreadButtonProps {
  onClick: () => void;
  isLoading?: boolean;
}

const CreateThreadButton: React.FC<CreateThreadButtonProps> = ({ 
  onClick, 
  isLoading = false 
}) => {
  return (
    <motion.div
      className="w-full"
      initial="initial"
      animate="animate"
      whileHover={isLoading ? "animate" : "hover"}
      whileTap={isLoading ? "animate" : "tap"}
      variants={{
        initial: { scale: 1 },
        animate: { scale: 1 },
        hover: { scale: 1.02 },
        tap: { scale: 0.98 }
      }}
    >
      <div 
        onClick={isLoading ? undefined : onClick}
        className={cn(
          "relative overflow-hidden backdrop-blur-md transition-all duration-300 group",
          "py-5 px-2 rounded-lg bg-gradient-to-br from-white/50 to-white/20 dark:from-slate-800/50 dark:to-slate-900/20", 
          "border border-white/20 dark:border-white/5",
          "w-full flex items-center justify-start gap-2 shadow-md",
          isLoading 
            ? "opacity-80 cursor-not-allowed" 
            : "cursor-pointer hover:from-white/70 hover:to-white/40 dark:hover:from-slate-800/70 dark:hover:to-slate-900/40 hover:shadow-lg"
        )}
      >
        {/* Background gradient animation */}
        <div 
          className={cn(
            "absolute inset-0 bg-gradient-to-r from-blue-100/10 via-purple-100/10 to-blue-100/10 dark:from-blue-900/10 dark:via-purple-900/10 dark:to-blue-900/10 gradient-animation transition-opacity duration-300",
            !isLoading && "group-hover:from-blue-100/30 group-hover:via-purple-100/30 group-hover:to-blue-100/30 dark:group-hover:from-blue-900/20 dark:group-hover:via-purple-900/20 dark:group-hover:to-blue-900/20"
          )}
        />
        
        {/* Icon with glow effect */}
        <div className="relative">
          <div className={cn(
            "absolute inset-0 bg-gradient-to-br from-blue-100/40 to-purple-100/40 dark:from-blue-900/20 dark:to-purple-900/20 rounded-full blur-md transition-opacity duration-300",
            isLoading ? "opacity-100" : "opacity-0 group-hover:opacity-100"
          )} />
          
          <motion.div
            animate={isLoading ? { rotate: 360 } : { rotate: 0 }}
            transition={isLoading ? { duration: 1, repeat: Infinity, ease: "linear" } : { duration: 0.2 }}
            className="relative z-10"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            ) : (
              <MessageSquarePlus className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            )}
          </motion.div>
        </div>
        
        {/* Text */}
        <span className="font-semibold text-sm text-slate-800 dark:text-slate-200 relative z-10">
          {isLoading ? 'Creating...' : 'New Conversation'}
        </span>
      </div>
    </motion.div>
  );
};

export default CreateThreadButton;
